import { theme } from '@/theme';

export const styles = {
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Solid semi-transparent overlay — NO backdropFilter blur (causes GPU lag on large tables)
    background: 'rgba(30, 27, 50, 0.55)',
    animation: 'fadeIn 0.12s ease-out',
    padding: theme.spacing.lg,
    // Isolate compositing to prevent re-painting the table behind
    willChange: 'opacity',
    contain: 'strict' as any,
  },

  modalCard: (maxWidthVal: number) => ({
    background: theme.colors.surface,
    borderRadius: theme.radius.xl,
    boxShadow: '0 20px 60px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)',
    width: '100%',
    maxWidth: maxWidthVal,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    // GPU-accelerated slide-down — no re-paint of backdrop
    animation: 'modalSlideIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
    willChange: 'transform, opacity',
    contain: 'layout style' as any,
  }),

  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.divider}`,
    flexShrink: 0,
  },

  modalTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text.heading,
  },

  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    background: theme.colors.surface,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.text.muted,
    transition: `all ${theme.animations.transitionFast}`,
    fontSize: '1rem',
  },

  modalBody: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '24px',
  },

  modalFooter: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.colors.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.md,
    flexShrink: 0,
  },

  confirmMessage: {
    color: theme.colors.text.body,
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
  },

  asterisk: {
    color: theme.colors.danger,
    marginLeft: 3,
  },

  selectPointer: {
    cursor: 'pointer',
  },

  /* Toast Notification Container & Items */
  toastContainer: {
    position: 'fixed' as const,
    bottom: 24,
    right: 24,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
    zIndex: 9999,
  },

  toastItem: (bgColor: string, borderColor: string) => ({
    background: bgColor,
    borderLeft: `4px solid ${borderColor}`,
    borderRadius: theme.radius.md,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    boxShadow: theme.shadows.md,
    minWidth: 300,
    maxWidth: 400,
    animation: 'fadeIn 0.2s ease-out',
    fontFamily: theme.typography.fontFamily,
  }),

  toastIcon: {
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.sm,
  },

  toastMessage: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.heading,
    flex: 1,
  },

  toastCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.colors.text.muted,
    fontSize: '1rem',
  },

  /* Data Table */
  tableLoadingWrapper: {
    padding: theme.spacing.xxxl,
  },

  tableLoadingRow: {
    display: 'flex',
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    alignItems: 'center',
  },

  tableOverflowWrapper: {
    overflowX: 'auto' as const,
  },

  paginationWrapper: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.colors.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: theme.typography.fontSize.xs,
    color: theme.colors.text.muted,
  },

  paginationCountBold: {
    color: theme.colors.text.heading,
  },

  paginationGroup: {
    display: 'flex',
    gap: 4,
  },

  paginationBtn: (isActive: boolean) => ({
    width: 32,
    height: 32,
    borderRadius: theme.radius.md,
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.semibold,
    background: isActive ? theme.colors.primary : theme.colors.bg,
    color: isActive ? theme.colors.text.inverse : theme.colors.text.muted,
    transition: `all ${theme.animations.transitionFast}`,
  }),

  pageHeaderActionsGroup: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },

  crudCardContainer: {
    padding: 0,
    overflow: 'hidden',
  },

  crudToolbar: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.colors.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 12,
  },

  crudSearchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    flexWrap: 'wrap' as const,
  },

  actionGroupRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 6,
  },

  editBtnIcon: {
    color: theme.colors.primary,
  },

  deleteBtnIcon: {
    color: theme.colors.danger,
  },

  viewBtnIcon: {
    color: theme.colors.success,
  },
} as const;
